import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { useAdaptiveCardStore } from './AdaptiveCardContext'
import { useRetorikStore } from './RetorikContext'

import { DeviceType, Routes, Theme } from '../../models/enums'
import { mobileDisplays, widgetDisplays, landscapeDisplays, borneDisplays, mediumBreakpoint } from '../../models/constants'
import type { ThemeColors } from '../../models/colorTypes'
import type { ViewsConfiguration } from '../../models/types'
import { defaultThemeDark } from '../../models/colors'

interface ViewState {
  visibility: string
  currentDeviceType: DeviceType
  isMobile: boolean
  isWidget: boolean
  isLandscape: boolean
  isBorne: boolean
  themeColors: ThemeColors
  theme: Theme
  configurations: ViewsConfiguration
  route: Routes
  currentHeight: number
  currentWidth: number
  isTactile: boolean
  isMobileLandscape: boolean
  displayControls: boolean
  mediumLandscape: boolean
  characterLoaded: boolean
  actions: {
    setVisibility: (value: 'visible' | 'hidden') => void
    toggleVisibility: () => void
    checkMobileLandscape: () => void
    setCurrentDeviceType: (value: DeviceType) => void
    setThemeColors: (value: ThemeColors) => void
    setTheme: (value: Theme) => void
    setRoute: (value: Routes) => void
    setViewsConfiguration: (value: ViewsConfiguration) => void
    setCurrentHeight: (value: number) => void
    setCurrentWidth: (value: number) => void
    setDisplayControls: (value: boolean) => void
    setCharacterLoaded: (value: boolean) => void
  }
}

const defaultConfiguration: ViewsConfiguration = {
  homeRoute: 'home',
  views: {
    home: {
      background: {
        image: '',
        style: 'image'
      }
    }
  }
}

const initialState = {
  visibility: 'visible',
  currentDeviceType: DeviceType.mobile,
  isMobile: false,
  isWidget: false,
  isLandscape: true,
  isBorne: false,
  themeColors: defaultThemeDark,
  theme: Theme.light,
  configurations: defaultConfiguration,
  route: Routes.Home,
  currentHeight: 1080,
  currentWidth: 1920,
  isTactile: window.matchMedia('(any-pointer: coarse)').matches,
  isMobileLandscape: false,
  displayControls: true,
  mediumLandscape: false,
  characterLoaded: false
}

const createViewStore = (enabledFeaturesRef: React.RefObject<Array<string>>, setAdaptiveCardHostConfig: (primary: string, background: string) => void) => {
  return createStore<ViewState>((set, get) => {
    return {
      ...initialState,
      actions: {
        setVisibility: (value: 'visible' | 'hidden'): void => {
          set({ visibility: value })
        },
        toggleVisibility: (): void => {
          set((state) => ({
            visibility: state.visibility === 'visible' ? 'hidden' : 'visible'
          }))
        },
        checkMobileLandscape: (): void => {
          set((state) => ({
            isMobileLandscape: state.currentDeviceType === DeviceType.mobile && state.currentHeight < state.currentWidth
          }))
        },
        setCurrentDeviceType: (value: DeviceType): void => {
          set({
            currentDeviceType: value,
            isMobile: mobileDisplays.includes(value),
            isWidget: widgetDisplays.includes(value),
            isLandscape: landscapeDisplays.includes(value),
            isBorne: borneDisplays.includes(value)
          })
          get().actions.checkMobileLandscape()
        },
        setThemeColors: (value: ThemeColors): void => {
          setAdaptiveCardHostConfig(value.primary, value.card.frame.background)

          set({
            themeColors: value
          })
        },
        setTheme: (value: Theme): void => {
          set({
            theme: value
          })
        },
        setRoute: (value: Routes): void => {
          if (value === Routes.News) {
            enabledFeaturesRef.current?.includes('news') ? set({ route: value }) : console.log('Retorik Framework > tried to switch to the news while the feature is not activated in Retorik Studio')
          } else {
            set({ route: value })
          }
        },
        setViewsConfiguration: (value: ViewsConfiguration): void => {
          set({ configurations: value })
        },
        setCurrentHeight: (value: number): void => {
          set({ currentHeight: value })
          get().actions.checkMobileLandscape()
        },
        setCurrentWidth: (value: number): void => {
          set({
            currentWidth: value,
            mediumLandscape: value < mediumBreakpoint
          })
          get().actions.checkMobileLandscape()
        },
        setDisplayControls: (value: boolean): void => {
          set({ displayControls: value })
        },
        setCharacterLoaded: (value: boolean): void => {
          set({ characterLoaded: value })
        }
      }
    }
  })
}

const ViewContext = createContext<StoreApi<ViewState> | null>(null)

const useViewStore = <T,>(selector: (state: ViewState) => T): T => {
  const store = useContext(ViewContext)
  if (!store) throw new Error('Retorik Framework > useViewStore must be used within ViewProvider')
  return useStore(store, selector)
}

const ViewProvider = ({ children }) => {
  const enabledFeatures = useRetorikStore((state) => state.enabledFeatures)
  const enabledFeaturesRef = useRef<Array<string>>(enabledFeatures)

  const setAdaptiveCardHostConfig = useAdaptiveCardStore((state) => state.actions.setAdaptiveCardHostConfig)
  const [store] = useState(() => createViewStore(enabledFeaturesRef, setAdaptiveCardHostConfig))

  useEffect(() => {
    enabledFeaturesRef.current = enabledFeatures
  }, [enabledFeatures])

  return <ViewContext.Provider value={store}>{children}</ViewContext.Provider>
}

export default ViewProvider
export { useViewStore }
